import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { UserPlus } from 'lucide-react';
import { AxiosError } from 'axios';
import {
  describeError,
  listOperators,
  problemCode,
  resendOperatorInvitation,
  type OperatorDto,
  type OperatorRole,
  type OperatorStatus,
} from '@/lib/api';
import { OperatorStatusBadge } from '@/components/OperatorStatusBadge';
import { Badge } from '@/components/ui';
import { SensitiveCredentialModal } from '@/components/SensitiveCredentialModal';
import { SimpleConfirmDialog } from '@/components/SimpleConfirmDialog';
import { OperatorCreatePage } from '@/pages/OperatorCreatePage';
import { CardTable, THead, TBody, Tr, Th, Td } from '@/components/CardTable';

const ROLE_OPTIONS: Array<OperatorRole | ''> = [
  '',
  'Admin',
  'Commercial',
  'SupportL2',
  'SupportL1',
];
const STATUS_OPTIONS: Array<OperatorStatus | ''> = [
  '',
  'Pending',
  'Active',
  'Revoked',
];

export function OperatorsListPage() {
  const [items, setItems] = useState<OperatorDto[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [role, setRole] = useState<OperatorRole | ''>('');
  const [status, setStatus] = useState<OperatorStatus | ''>('');
  // Dois erros, porque sao de donos diferentes: o da busca vale enquanto for
  // o da lista que esta na tela, e o da acao vale ate a proxima acao. Antes
  // dividiam um estado so, e o `setError(null)` no inicio do efeito — que era
  // o apontamento — apagava um erro de reenvio quando a lista recarregava.
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  // Inline resend flow state (G1 of operator-resend-invite-quickaction)
  const [resendTarget, setResendTarget] = useState<OperatorDto | null>(null);
  const [resendSubmitting, setResendSubmitting] = useState(false);
  const [credentialModal, setCredentialModal] = useState<{
    verificationCode: string;
    operatorEmail: string;
    operatorFullName: string;
  } | null>(null);

  const pageSize = 20;

  async function handleResendConfirm() {
    if (!resendTarget) return;
    setResendSubmitting(true);
    setActionError(null);
    try {
      const resp = await resendOperatorInvitation(resendTarget.id);
      setCredentialModal({
        verificationCode: resp.verificationCode,
        operatorEmail: resendTarget.email,
        operatorFullName: resendTarget.fullName,
      });
      setResendTarget(null);
      setReloadTick((t) => t + 1);
    } catch (err) {
      setActionError(describeResendError(err));
      setResendTarget(null);
    } finally {
      setResendSubmitting(false);
    }
  }

  const requestKey = `${page}:${role}:${status}:${reloadTick}`;
  const loading = loadedKey !== requestKey;
  const error = actionError ?? (loadedKey === requestKey ? loadError : null);

  useEffect(() => {
    let cancelled = false;
    listOperators({
      page,
      pageSize,
      role: role || undefined,
      status: status || undefined,
    })
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        setTotal(data.total);
        setLoadError(null);
      })
      .catch((err) => !cancelled && setLoadError(describeError(err)))
      .finally(() => !cancelled && setLoadedKey(requestKey));
    return () => {
      cancelled = true;
    };
  }, [page, role, status, reloadTick, requestKey]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);
  const filtersActive = role !== '' || status !== '';

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div className="filter-inline">
          <div className="field">
            <label htmlFor="op-role">Papel</label>
            <select
              id="op-role"
              value={role}
              onChange={(e) => {
                setRole(e.target.value as OperatorRole | '');
                setPage(1);
              }}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r === '' ? 'Todos' : translateRole(r)}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="op-status">Status</label>
            <select
              id="op-status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as OperatorStatus | '');
                setPage(1);
              }}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s === '' ? 'Todos' : translateStatus(s)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button type="button" className="btn" onClick={() => setShowCreate(true)}>
          <UserPlus size={18} /> Criar operador
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="card card-flush">
        <div className="table-scroll">
          <CardTable>
            <THead>
              <Tr>
                <Th>Operador</Th>
                <Th>Papel</Th>
                <Th>Status</Th>
                <Th>Último login</Th>
                <Th></Th>
              </Tr>
            </THead>
            <TBody>
              {loading && (
                <Tr>
                  <Td colSpan={5} style={{ textAlign: 'center', padding: 24 }} className="muted">
                    Carregando…
                  </Td>
                </Tr>
              )}
              {!loading && items.length === 0 && total === 0 && !filtersActive && (
                <Tr>
                  <Td colSpan={5} style={{ textAlign: 'center', padding: 32 }}>
                    <div style={{ fontSize: 16, marginBottom: 8, fontWeight: 600 }}>
                      Boas-vindas. Comece criando seu primeiro operador.
                    </div>
                    <button type="button" className="btn" onClick={() => setShowCreate(true)}>
                      Criar operador
                    </button>
                  </Td>
                </Tr>
              )}
              {!loading && items.length === 0 && filtersActive && (
                <Tr>
                  <Td colSpan={5} style={{ textAlign: 'center', padding: 24 }} className="muted">
                    Nenhum operador encontrado com esses filtros.{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setRole('');
                        setStatus('');
                        setPage(1);
                      }}
                      style={{ background: 'none', border: 'none', color: 'var(--color-primary-text)', cursor: 'pointer' }}
                    >
                      Limpar filtros
                    </button>
                  </Td>
                </Tr>
              )}
              {!loading && items.map((op) => (
                <Tr key={op.id}>
                  <Td heading>
                    <div style={{ fontWeight: 600 }}>{op.fullName}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{op.email}</div>
                  </Td>
                  <Td label="Papel">
                    <Badge tone={op.role === 'Admin' ? 'primary' : op.role === 'Commercial' ? 'info' : 'neutral'}>
                      {translateRole(op.role)}
                    </Badge>
                  </Td>
                  <Td label="Status">
                    <OperatorStatusBadge
                      status={op.status}
                      pendingExpiresAt={op.pendingInvitationExpiresAt}
                    />
                  </Td>
                  <Td label="Último login">
                    {op.lastLoginAt
                      ? new Date(op.lastLoginAt).toLocaleString('pt-BR')
                      : '—'}
                  </Td>
                  <Td action>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      {op.status === 'Pending' && (
                        <button
                          type="button"
                          onClick={() => setResendTarget(op)}
                          title="Invalida o link anterior e dispara novo email"
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: 'var(--color-secondary-dark)',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: 13,
                          }}
                        >
                          Reenviar
                        </button>
                      )}
                      <Link to={`/operators/${op.id}`}>Detalhe →</Link>
                    </div>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </CardTable>
        </div>
      </div>

      <div className="pagination">
        <span className="pagination-info">
          {total} {total === 1 ? 'operador' : 'operadores'} · página {page} de {totalPages}
        </span>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1 || loading}
        >
          Anterior
        </button>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages || loading}
        >
          Próxima
        </button>
      </div>

      {showCreate && (
        <OperatorCreatePage
          onClose={() => {
            setShowCreate(false);
            setReloadTick((t) => t + 1);
          }}
        />
      )}

      {resendTarget && (
        <SimpleConfirmDialog
          title="Reenviar convite"
          body={
            <>
              Reenviar convite para <strong>{resendTarget.email}</strong>? Esta ação
              invalida o link anterior e dispara novo email.
            </>
          }
          confirmLabel="Reenviar"
          submitting={resendSubmitting}
          onConfirm={handleResendConfirm}
          onCancel={() => setResendTarget(null)}
        />
      )}

      {credentialModal && (
        <SensitiveCredentialModal
          verificationCode={credentialModal.verificationCode}
          operatorEmail={credentialModal.operatorEmail}
          operatorFullName={credentialModal.operatorFullName}
          contextLabel="Convite reenviado"
          onConfirm={() => setCredentialModal(null)}
        />
      )}
    </div>
  );
}

function translateRole(r: OperatorRole): string {
  switch (r) {
    case 'Admin':
      return 'Admin';
    case 'Commercial':
      return 'Comercial';
    case 'SupportL2':
      return 'Suporte N2';
    case 'SupportL1':
      return 'Suporte N1';
  }
}

function translateStatus(s: OperatorStatus): string {
  switch (s) {
    case 'Pending':
      return 'Pendente de ativação';
    case 'Active':
      return 'Ativo';
    case 'Revoked':
      return 'Revogado';
  }
}

function describeResendError(err: unknown): string {
  if (err instanceof AxiosError) {
    const code = problemCode(err);
    if (code === 'platform.accounts.resend_rate_limited') {
      const data = err.response?.data as { retryAfterSeconds?: number } | undefined;
      const seconds = data?.retryAfterSeconds ?? 0;
      const minutes = Math.max(1, Math.ceil(seconds / 60));
      return `Limite de reenvios atingido para esta conta. Tente novamente em ~${minutes} min ou use "Redefinir senha" pelo detalhe.`;
    }
    const status = err.response?.status ?? 0;
    if (status === 422) {
      return 'Operador já está ativo — não é mais necessário reenviar convite.';
    }
    if (status === 404) {
      return 'Operador não encontrado.';
    }
  }
  return describeError(err);
}
